const AUTOMATION_EXTENSION_HOOK = 'automation-enhanced:config';

const dedupeByKey = items => {
  const seen = new Map();
  items.forEach(item => {
    if (item?.key) seen.set(item.key, item);
  });
  return Array.from(seen.values());
};

const isPlainObject = value =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const mergeEntityAttributes = (
  baseAttributes = {},
  extensionAttributes = {}
) => {
  const merged = { ...baseAttributes };

  Object.entries(extensionAttributes).forEach(([entityKey, attributes]) => {
    if (!isPlainObject(attributes) || !isPlainObject(merged[entityKey])) {
      merged[entityKey] = attributes;
      return;
    }

    merged[entityKey] = {
      ...merged[entityKey],
      ...attributes,
    };
  });

  return merged;
};

const mergeArrayRecords = (baseRecords = {}, extensionRecords = {}) => {
  const merged = { ...baseRecords };

  Object.entries(extensionRecords).forEach(([key, values]) => {
    if (!Array.isArray(values) || !Array.isArray(merged[key])) {
      merged[key] = values;
      return;
    }

    merged[key] = [...new Set([...(merged[key] || []), ...values])];
  });

  return merged;
};

const mergeActionTypes = (baseTypes = [], extensionTypes = []) =>
  dedupeByKey([...(baseTypes || []), ...(extensionTypes || [])]);

const mergeEventContexts = (baseContexts = {}, extensionContexts = {}) => {
  const merged = { ...baseContexts };

  Object.entries(extensionContexts).forEach(([key, value]) => {
    if (!merged[key]) {
      merged[key] = value;
      return;
    }

    merged[key] = {
      ...merged[key],
      ...value,
      events: [
        ...new Set([...(merged[key].events || []), ...(value.events || [])]),
      ],
    };
  });

  return merged;
};

const mergeConfig = (baseConfig, extension = {}) => ({
  ...baseConfig,
  entityAttributes: mergeEntityAttributes(
    baseConfig.entityAttributes,
    extension.entityAttributes
  ),
  eventContexts: mergeEventContexts(
    baseConfig.eventContexts,
    extension.eventContexts
  ),
  eventEntities: mergeArrayRecords(
    baseConfig.eventEntities,
    extension.eventEntities
  ),
  actionsByEntity: mergeArrayRecords(
    baseConfig.actionsByEntity,
    extension.actionsByEntity
  ),
  automationRuleEvents: dedupeByKey([
    ...(baseConfig.automationRuleEvents || []),
    ...(extension.automationRuleEvents || []),
  ]),
  actionTypes: mergeActionTypes(baseConfig.actionTypes, extension.actionTypes),
  customAttributeEvents: [
    ...new Set([
      ...(baseConfig.customAttributeEvents || []),
      ...(extension.customAttributeEvents || []),
    ]),
  ],
});

const getPluginSystem = () =>
  window.chatwoot?.PluginSystem || window.PluginSystem || null;

export const resolveAutomationConfig = baseConfig => {
  const pluginSystem = getPluginSystem();

  if (!pluginSystem?.executeHook) {
    return baseConfig;
  }

  return (
    pluginSystem.executeHook(AUTOMATION_EXTENSION_HOOK, baseConfig) ||
    baseConfig
  );
};

export const registerAutomationExtension = (
  extension,
  pluginSystem = getPluginSystem()
) => {
  if (!pluginSystem?.registerHook) {
    return false;
  }

  pluginSystem.registerHook(AUTOMATION_EXTENSION_HOOK, config =>
    mergeConfig(config, extension)
  );

  return true;
};

export { AUTOMATION_EXTENSION_HOOK, mergeConfig };
